import { useCallback, useMemo } from "react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import useProfiler from "../../../../hooks/useProfiler.js";
export function useBreakdownController(props) {
    const { isSolo, travellersCount } = useProfiler();
    return useBreakdownInnerController({
        ...props,
        profiler: {
            isSolo: isSolo(),
            travellersCount: travellersCount()
        }
    });
}
export function useBreakdownInnerController(props) {
    const { price, items, addedItems, footerSections, isHungaryDepartingRyanAir, isSummary, expandedByDefault, profiler: { isSolo, travellersCount } } = props;
    const i18n = useI18n();
    const getSectionTitle = useCallback((id) => {
        switch (id) {
            case "FEE":
                return i18n.trans(t("flights_price_details_fees_title"));
            case "EXTRA":
                return i18n.trans(t("flights_price_details_heading_extras"));
            case "DISCOUNT":
                return i18n.trans(t("flights_price_details_heading_discounts"));
            case "BOOKING_CHANGES":
                return i18n.trans(t("flights_price_details_heading_booking_changes"));
            default: {
                const travellerCount = travellersCount;
                return isSolo
                    ? i18n.trans(t("flights_price_details_heading_flight_solo"))
                    : i18n.trans(t("flights_price_details_heading_flight_multi", {
                        variables: { num_travellers: travellerCount },
                        num_exception: travellerCount
                    }));
            }
        }
    }, [i18n, isSolo, travellersCount]);
    const { flightAdultItems, flightChildItems, flightInfantItems, extraItems, discountItems, feeItems, bookingChangesItems } = useMemo(() => {
        const feeItems = items.filter((item) => item.scope === "FEE") || [];
        const extraItems = items.filter((item) => item.scope === "EXTRA") || [];
        const discountItems = items.filter((item) => item.scope === "DISCOUNT") || [];
        const flightAdultItems = items.filter((item) => item.scope === "FLIGHT_ADULT") || [];
        const flightChildItems = items.filter((item) => item.scope === "FLIGHT_CHILD") || [];
        const flightInfantItems = items.filter((item) => item.scope === "FLIGHT_INFANT") || [];
        const bookingChangesItems = items.filter((item) => item.scope === "BOOKING_CHANGES") || [];
        return {
            flightAdultItems,
            flightChildItems,
            flightInfantItems,
            extraItems,
            discountItems,
            feeItems,
            bookingChangesItems
        };
    }, [items]);
    const { extraAddedItems, discountAddedItems, feeAddedItems } = useMemo(() => {
        const feeAddedItems = addedItems.filter((item) => item.scope === "FEE") || [];
        const extraAddedItems = addedItems.filter((item) => item.scope === "EXTRA") || [];
        const discountAddedItems = addedItems.filter((item) => item.scope === "DISCOUNT") || [];
        return { extraAddedItems, discountAddedItems, feeAddedItems };
    }, [addedItems]);
    const isExpandedByDefault = useCallback((section) => {
        switch (section) {
            case "FEE":
            case "EXTRA":
            case "DISCOUNT":
            case "FLIGHT_ADULT":
            case "FLIGHT_CHILD":
            case "FLIGHT_INFANT":
            case "BOOKING_CHANGES":
                return !!expandedByDefault?.[section];
            case "FLIGHT":
                return (!!expandedByDefault?.FLIGHT_ADULT || !!expandedByDefault?.FLIGHT_CHILD || !!expandedByDefault?.FLIGHT_INFANT);
            default:
                return false;
        }
    }, [expandedByDefault]);
    const sections = useMemo(() => {
        const allSections = {};
        const fee = [...feeItems, ...feeAddedItems];
        const extra = [...extraItems, ...extraAddedItems];
        const discount = [...discountItems, ...discountAddedItems];
        const flight = [...flightAdultItems, ...flightChildItems, ...flightInfantItems];
        const bookingChanges = [...bookingChangesItems];
        if (fee.length)
            allSections.FEE = {
                id: "FEE",
                name: getSectionTitle("FEE"),
                items: fee,
                expandedByDefault: isExpandedByDefault("FEE")
            };
        if (extra.length)
            allSections.EXTRA = {
                id: "EXTRA",
                name: getSectionTitle("EXTRA"),
                items: extra,
                expandedByDefault: isExpandedByDefault("EXTRA")
            };
        if (flight.length)
            allSections.FLIGHT = {
                id: "FLIGHT",
                name: getSectionTitle(),
                items: flight,
                expandedByDefault: isExpandedByDefault("FLIGHT")
            };
        if (discount.length)
            allSections.DISCOUNT = {
                id: "DISCOUNT",
                name: getSectionTitle("DISCOUNT"),
                items: discount,
                expandedByDefault: isExpandedByDefault("DISCOUNT")
            };
        if (bookingChanges.length)
            allSections.BOOKING_CHANGES = {
                id: "BOOKING_CHANGES",
                name: getSectionTitle("BOOKING_CHANGES"),
                items: bookingChanges,
                expandedByDefault: isExpandedByDefault("BOOKING_CHANGES")
            };
        return allSections;
    }, [
        feeItems,
        extraItems,
        feeAddedItems,
        discountItems,
        extraAddedItems,
        flightAdultItems,
        flightChildItems,
        flightInfantItems,
        discountAddedItems,
        bookingChangesItems,
        isExpandedByDefault,
        getSectionTitle
    ]);
    const totalPriceTitle = useMemo(() => i18n.trans(t("flights_price_details_total_title")), [i18n]);
    const totalPriceFooter = useMemo(() => isHungaryDepartingRyanAir
        ? i18n.trans(t("flights_ryanair_hungary_sr_price_single"))
        : i18n.trans(t("flights_price_details_includes_taxes")), [isHungaryDepartingRyanAir, i18n]);
    const totalPrice = useMemo(() => {
        const items = [...extraAddedItems, ...discountAddedItems, ...feeAddedItems].map((item) => item.price);
        return items.reduce((prev, current) => pricingUtils.sumPrice(prev, current), price);
    }, [price, feeAddedItems, extraAddedItems, discountAddedItems]);
    return {
        sections,
        totalPrice,
        footerSections,
        totalPriceTitle,
        totalPriceFooter,
        isSummary
    };
}
